import type { AddressPayload } from '~~/layers/base/stores/contact.store'
import type {
    CheckoutCartAddress,
    CheckoutJourney,
    CheckoutReturnAddressSyncResult,
    PersistStripeBillingAddressResult,
} from '~~/layers/base/types/checkout'
import { shouldPersistStripeBillingAddressToProfile } from '~~/layers/base/utils/checkoutJourneyPolicy'
import { mapCheckoutCartAddressToAddressPayload } from '~~/layers/base/utils/mapCheckoutCartAddressToAddressPayload'

type PersistStripeBillingAddressInput = {
    /** Stripe billing Address Element value (payer). May also seed profile return / cart shipping. */
    billingAddress: CheckoutCartAddress
    journey: CheckoutJourney | null
    isLoggedIn: boolean
    activeCartId: string | null
}

type PersistStripeBillingAddressDeps = {
    createAddress: (payload: AddressPayload) => Promise<{
        addressId: string | null
        error: string | null
    }>
    syncReturnAddressToCart: (overrides: {
        returnAddressId: string
    }) => Promise<CheckoutReturnAddressSyncResult>
}

/**
 * Persists the Stripe billing Address Element's value to profile return addresses, then syncs it
 * onto the ACCS cart (billing always; shipping too on journeys that map return → shipping).
 *
 * Renamed from `persistStripeDelivery` / `PersistStripeDeliveryInput.shippingAddress` (TK-12335):
 * in this app "shipping" means recipient/delivery address, but this path only handles the payer's
 * *billing* Address Element (and may seed profile return / ACCS billing). Calling it "delivery"
 * with a `shippingAddress` field implied the wrong address.
 *
 * Tax (ACCS/Magento) is destination-based on the **cart shipping** address, not the Stripe label:
 * - **handwritten_note** (`handwriting_project_id` set): this call updates billing only;
 *   recipient shipping (and tax) stay on the note project — see `shouldSyncReturnAddressToShipping`.
 * - **handwriting_stationery** (no note project): sync also sets ACCS shipping from this same
 *   billing value, so tax destination follows that address after persist.
 *
 * Signed-in `handwriting_stationery` / `handwritten_note` / `handwritten_note_bulk` only: map
 * address → `POST /api/addresses` (dedupe + default on new insert) → `sync-return-address` with
 * `return_address_id`.
 */
export async function persistStripeBillingAddress(
    input: PersistStripeBillingAddressInput,
    deps: PersistStripeBillingAddressDeps
): Promise<PersistStripeBillingAddressResult> {
    if (!input.isLoggedIn) {
        return { ok: false, skipped: 'not_signed_in' }
    }
    if (!shouldPersistStripeBillingAddressToProfile(input.journey)) {
        return { ok: false, skipped: 'unsupported_journey' }
    }
    if (!input.activeCartId?.trim()) {
        return { ok: false, skipped: 'no_active_cart' }
    }

    const payload = mapCheckoutCartAddressToAddressPayload(input.billingAddress)
    const created = await deps.createAddress(payload)
    if (created.error || !created.addressId) {
        return {
            ok: false,
            error: created.error ?? 'Failed to save return address',
        }
    }

    const syncResult = await deps.syncReturnAddressToCart({
        returnAddressId: created.addressId,
    })
    if (!syncResult.ok) {
        if ('skipped' in syncResult) {
            if (syncResult.skipped === 'no_active_cart') {
                return { ok: false, skipped: 'no_active_cart' }
            }
            return { ok: false, skipped: 'unsupported_journey' }
        }
        return { ok: false, error: syncResult.error }
    }

    return {
        ok: true,
        addressId: created.addressId,
        cartId: syncResult.cartId,
    }
}
